import { HTTP_METHOD, JSON_HEADERS, PAYMENT_PAGE_API_PATH } from "./paymentPage.constants";

const DEFAULT_API_BASE_URL = process.env.REACT_APP_API_URL;
// Test aid, to be removed with the test: a link may name the Jellyfish origin it was issued by, as
// "?apiOrigin=<origin>". It is honoured only when that origin is listed here; any other named origin is
// refused, never swapped for the default.
const API_ORIGIN_ALLOWLIST = Object.freeze(["https://tygertyger.ngrok.app"]);
export const API_ORIGIN_PARAMETER = "apiOrigin";
const OBJECT_TYPE = "object";
const TEXT_TYPE = "string";
const PATH_SEPARATOR = "/";
const NO_REFUSAL = "NONE";
const GET_OPTIONS = Object.freeze({ method: HTTP_METHOD.GET });
const POST_WITHOUT_BODY_OPTIONS = Object.freeze({ method: HTTP_METHOD.POST });

export class PaymentPageRequestError extends Error {
    constructor(httpStatus, refusal, message) {
        super(message);
        this.name = "PaymentPageRequestError";
        this.httpStatus = httpStatus;
        this.refusal = refusal;
    }
}

function isObject(value) {
    if (typeof value !== OBJECT_TYPE) {
        return false;
    }
    return value !== null;
}

function readRefusal(responseBody) {
    if (isObject(responseBody.data) === false) {
        return NO_REFUSAL;
    }
    if (typeof responseBody.data.refusal !== TEXT_TYPE) {
        return NO_REFUSAL;
    }
    return responseBody.data.refusal;
}

export function readRequestedApiOrigin(search) {
    const requested = new URLSearchParams(search).get(API_ORIGIN_PARAMETER);
    if (requested === null || requested.length === 0) return null;
    let parsed;
    try {
        parsed = new URL(requested);
    } catch {
        throw new Error("This payment link names a payment API address that is not valid.");
    }
    if (parsed.origin !== requested || parsed.protocol !== "https:") throw new Error("This payment link names a payment API address that is not valid.");
    return parsed.origin;
}

export function resolveApiBaseUrl(search) {
    const requested = readRequestedApiOrigin(search === undefined ? window.location.search : search);
    if (requested === null) return DEFAULT_API_BASE_URL;
    if (!API_ORIGIN_ALLOWLIST.includes(requested)) throw new Error("This payment link names a payment API this page does not serve.");
    return requested;
}

function buildPaymentLinkPageAddress(paymentLinkId) {
    const apiBaseUrl = resolveApiBaseUrl();
    if (typeof apiBaseUrl !== "string" || apiBaseUrl.length === 0) throw new Error("Payment API URL is not configured.");
    const base = new URL(apiBaseUrl);
    if (base.protocol !== "https:" && !(["localhost", "127.0.0.1"].includes(base.hostname) && base.protocol === "http:")) throw new Error("Payment API URL must use HTTPS.");
    return apiBaseUrl.replace(/\/$/, "") + PAYMENT_PAGE_API_PATH.PAYMENT_LINKS + encodeURIComponent(paymentLinkId);
}

function buildPaymentLinkAddress(paymentLinkId, pathSuffix) {
    return buildPaymentLinkPageAddress(paymentLinkId) + pathSuffix;
}

function buildAttemptPath(attemptId, actionPath) {
    return PAYMENT_PAGE_API_PATH.ATTEMPTS + PATH_SEPARATOR + encodeURIComponent(attemptId) + actionPath;
}

function buildPostOptions(requestBody) {
    return { method: HTTP_METHOD.POST, headers: JSON_HEADERS, body: JSON.stringify(requestBody) };
}

async function sendPaymentPageRequest(address, requestOptions) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    try {
        const response = await fetch(address, { ...requestOptions, signal: controller.signal });
        const responseBody = await response.json();
        if (response.ok === false) {
            throw new PaymentPageRequestError(response.status, readRefusal(responseBody), responseBody.message);
        }
        if (!isObject(responseBody) || !isObject(responseBody.data)) throw new Error("Invalid payment API response.");
        return responseBody.data;
    } finally {
        window.clearTimeout(timeout);
    }
}

export function fetchPaymentLinkPage(paymentLinkId) {
    return sendPaymentPageRequest(buildPaymentLinkPageAddress(paymentLinkId), GET_OPTIONS);
}

export function openCheckoutSession(paymentLinkId) {
    return sendPaymentPageRequest(
        buildPaymentLinkAddress(paymentLinkId, PAYMENT_PAGE_API_PATH.CHECKOUT_SESSIONS),
        POST_WITHOUT_BODY_OPTIONS
    );
}

export function startPaymentAttempt(paymentLinkId, checkoutSession, billingAddress) {
    return sendPaymentPageRequest(
        buildPaymentLinkAddress(paymentLinkId, PAYMENT_PAGE_API_PATH.ATTEMPTS),
        buildPostOptions({ sessionId: checkoutSession.id, sessionData: checkoutSession.data, billingAddress })
    );
}

export function sendBankCheckStart(paymentLinkId, attemptId) {
    return sendPaymentPageRequest(
        buildPaymentLinkAddress(paymentLinkId, buildAttemptPath(attemptId, PAYMENT_PAGE_API_PATH.BANK_CHECK)),
        POST_WITHOUT_BODY_OPTIONS
    );
}

export function sendPaymentDetails(paymentLinkId, attemptId, paymentData) {
    return sendPaymentPageRequest(
        buildPaymentLinkAddress(paymentLinkId, buildAttemptPath(attemptId, PAYMENT_PAGE_API_PATH.DETAILS)),
        buildPostOptions({ data: paymentData })
    );
}

export function sendPaymentResult(paymentLinkId, attemptId, sessionResult) {
    return sendPaymentPageRequest(
        buildPaymentLinkAddress(paymentLinkId, buildAttemptPath(attemptId, PAYMENT_PAGE_API_PATH.RESULT)),
        buildPostOptions({ sessionResult })
    );
}
